import React from 'react';
import * as Sentry from '@sentry/react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    // Send to Sentry so we can see the exact error remotely
    try {
      if (Sentry.isInitialized()) {
        Sentry.withScope(scope => {
          scope.setTag('source', 'ErrorBoundary');
          scope.setExtra('componentStack', errorInfo?.componentStack);
          // Attach any persisted booking carts so date/state-shaped crashes
          // (e.g. "Invalid time value") carry the offending values remotely.
          try {
            const carts = {};
            for (let i = 0; i < sessionStorage.length; i++) {
              const key = sessionStorage.key(i);
              if (key && key.startsWith('bookingData-')) {
                carts[key] = sessionStorage.getItem(key)?.slice(0, 2000);
              }
            }
            if (Object.keys(carts).length > 0) scope.setExtra('bookingData', carts);
          } catch { /* sessionStorage may be unavailable */ }
          Sentry.captureException(error);
        });
      }
    } catch { /* don't let Sentry break the error boundary */ }
  }

  handleRetry() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-6">An unexpected error occurred. Please try again.</p>
            <p className="text-xs text-gray-400 mb-4 break-all">{this.state.error?.message}</p>
            <button
              onClick={this.handleRetry}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
